import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { Link as RouterLink } from "react-router";
import SectionReveal from "../components/SectionReveal";

const NAVY = "#102B63";
const NAVY_LIGHT = "#2F4E8B";
const SOFT_TEXT = "#607493";
const CTA_ORANGE = "#F5A623";
const CTA_ORANGE_HOVER = "#E79C1F";

const highlights = [
  {
    icon: <WorkOutlineRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Conseil & accompagnement",
    text: "Axiontek accompagne les entreprises dans la structuration, l’exécution et la réussite de leurs initiatives technologiques.",
  },
  {
    icon: <HubRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Expertises ciblées",
    text: "Des compétences à forte valeur ajoutée, précisément alignées avec les enjeux métiers et techniques.",
  },
  {
    icon: <TipsAndUpdatesRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Innovation produit",
    text: "Axiontek développe également ses propres solutions, comme CleverSched dédiée à la gestion des activités et des équipes.",
  },
];

function HighlightCard({ item }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 3.5, md: 4 },
        bgcolor: "rgba(255,255,255,0.98)",
        border: "1px solid rgba(16,43,99,0.08)",
        boxShadow: "0 14px 34px rgba(16,43,99,0.05)",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 3.6 },
          minHeight: { xs: 230, md: 255 },
        }}
      >
        <Box
          sx={{
            px: { xs: 0.4, md: 0.8 },
            pt: { xs: 0.4, md: 0.6 },
            pb: { xs: 0.2, md: 0.3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              mb: 1.6,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(16,43,99,0.07)",
                color: NAVY,
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>

            <Typography
              sx={{
                color: NAVY,
                fontWeight: 800,
                fontSize: { xs: "1.02rem", md: "1.08rem" },
                lineHeight: 1.3,
                maxWidth: "90%",
              }}
            >
              {item.title}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: SOFT_TEXT,
              fontSize: { xs: "0.97rem", md: "1rem" },
              lineHeight: 1.92,
              maxWidth: "95%",
              pl: { xs: 0.1, md: 0.2 },
              pr: { xs: 0.2, md: 0.5 },
            }}
          >
            {item.text}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function HomePage() {
  return (
    <Box sx={{ pt: { xs: 4, md: 7 }, pb: { xs: 8, md: 10 } }}>
      <Container maxWidth="xl">
        <SectionReveal>
          <Paper
            sx={{
              borderRadius: { xs: 4.5, md: 5 },
              px: { xs: 2.8, sm: 3.6, md: 5.4 },
              py: { xs: 3.3, sm: 4.1, md: 4.9 },
              background:
                "linear-gradient(135deg, #FFFFFF 0%, #F6F8FD 45%, #EEF3FB 100%)",
              border: "1px solid rgba(16,43,99,0.08)",
              boxShadow: "0 18px 45px rgba(16,43,99,0.08)",
              position: "relative",
              overflow: "hidden",
              mb: 3.8,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -70,
                right: -70,
                width: 190,
                height: 190,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(47,78,139,0.08), transparent 70%)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.76rem", md: "0.86rem" },
                  fontWeight: 800,
                  color: CTA_ORANGE,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  mb: 1.5,
                  lineHeight: 1.2,
                }}
              >
                AXIONTEK
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  fontSize: {
                    xs: "2rem",
                    sm: "2.7rem",
                    md: "3.2rem",
                    lg: "3.7rem",
                  },
                  lineHeight: { xs: 1.08, md: 1.04 },
                  fontWeight: 800,
                  letterSpacing: "-0.04em",
                  color: NAVY,
                  maxWidth: 980,
                  mb: 2.1,
                }}
              >
                <Box component="span" sx={{ display: "block" }}>
                  Accélérer la performance
                </Box>
                <Box component="span" sx={{ display: "block", color: NAVY_LIGHT }}>
                  de vos projets IT
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.06rem" },
                  lineHeight: 1.9,
                  color: SOFT_TEXT,
                  maxWidth: 920,
                  mb: 3.2,
                }}
              >
                Axiontek accompagne les entreprises dans la structuration,
                l’exécution et la réussite de leurs initiatives technologiques en
                mobilisant des expertises à forte valeur ajoutée, précisément
                alignées avec leurs enjeux.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  component={RouterLink}
                  to="/a-propos"
                  variant="contained"
                  sx={{
                    bgcolor: CTA_ORANGE,
                    color: NAVY,
                    fontWeight: 800,
                    borderRadius: "999px",
                    px: 2.8,
                    py: 1.2,
                    boxShadow: "0 14px 28px rgba(245,166,35,0.24)",
                    "&:hover": {
                      bgcolor: CTA_ORANGE_HOVER,
                    },
                  }}
                >
                  Découvrir Axiontek
                </Button>

                <Button
                  component={RouterLink}
                  to="/produits"
                  variant="outlined"
                  sx={{
                    borderRadius: "999px",
                    px: 2.8,
                    py: 1.15,
                    fontWeight: 700,
                    color: NAVY,
                    borderColor: "rgba(16,43,99,0.16)",
                    backgroundColor: "rgba(255,255,255,0.72)",
                    "&:hover": {
                      borderColor: "rgba(16,43,99,0.28)",
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                >
                  Voir nos produits
                </Button>
              </Box>
            </Box>
          </Paper>
        </SectionReveal>

        <SectionReveal delay={0.08}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 2, md: 2.4 },
              alignItems: "stretch",
            }}
          >
            {highlights.map((item) => (
              <HighlightCard key={item.title} item={item} />
            ))}
          </Box>
        </SectionReveal>
      </Container>
    </Box>
  );
}